import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import type { AlchemystProps } from '@/types/appStates';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

const fetchMyIntegrationInstances = async (
  persona: AlchemystProps['persona']
): Promise<{ integrations: Record<string, any>[] }> => {
  const fetchMyIntegrationInstancesResponse = await fetchWithRewrites(
    `/api/v1/persona/${persona}/integrations`
  );

  if (fetchMyIntegrationInstancesResponse.ok) {
    return fetchMyIntegrationInstancesResponse.json();
  }

  return { integrations: [] };
};

function MyIntegrations(): React.ReactNode {
  const [installedIntegrations, setInstalledIntegrations] = useState<
    Record<string, any>[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { currentAlchemyst } = useAlchemystStoreForAi((store) => ({
    currentAlchemyst: store.alchemyst,
  }));

  const fetchMyIntegrations = async (persona: AlchemystProps['persona']) => {
    setIsLoading(true);
    const myIntegrationInstances = await fetchMyIntegrationInstances(persona);
    setInstalledIntegrations(myIntegrationInstances.integrations ?? []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMyIntegrations(currentAlchemyst.persona);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 2 }}>
      {isLoading ? (
        <CircularProgress />
      ) : installedIntegrations.length === 0 ? (
        <Typography>No integrations found</Typography>
      ) : (
        installedIntegrations.map((integration, index) => (
          <Card key={index} sx={{ p: 2 }}>
            <CardContent>
              <Typography gutterBottom variant="h6">
                {integration.name}
              </Typography>
              <Typography color="text.secondary">
                Persona: {integration.persona}
              </Typography>
              <Typography color="text.secondary">
                Email: {integration.email}
              </Typography>
              {integration.phoneNumber ? (
                <Typography color="text.secondary">
                  Phone: {integration.phoneNumber}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
}

export default MyIntegrations;
